import React, { useEffect } from 'react';
import { Box, Card, CardContent, Stack, Alert, Snackbar } from '@mui/material';
import { PageContainer } from '../../common/components';
import { useCreateWorkflowMutation } from '../../../services/WorkflowService';
import { initializeWorkflow, addStep, clearWorkflow } from '../state';
import { WorkflowStepType, toCreateRequest, createDefaultStep } from '../../../services/WorkflowService';
import { WorkflowCreateHeader, BasicInfoSection, ConditionsSection, StepsSection, UnsavedChangesDialog } from '../components';
import { WorkflowValidationProvider } from '../components/shared';
import { useWorkflowForm } from '../hooks/useWorkflowForm';

const SUCCESS_MESSAGE = 'Workflow created successfully!';

/** Inner form wrapped by WorkflowValidationProvider */
const WorkflowCreateForm: React.FC = () => {
  const {
    dispatch, currentWorkflow,
    showSuccess, setShowSuccess,
    errorMessage, clearError,
    validateAndSubmit,
    showDialog, confirmLeave, cancelLeave, guardedNavigate,
  } = useWorkflowForm();

  const [createWorkflow] = useCreateWorkflowMutation();

  useEffect(() => {
    dispatch(initializeWorkflow());
    dispatch(addStep(createDefaultStep(WorkflowStepType.AutoAssignmentStep, 1)));
    return () => { dispatch(clearWorkflow()); };
  }, [dispatch]);

  const handleSubmit = () => validateAndSubmit(async () => {
    await createWorkflow(toCreateRequest(currentWorkflow!)).unwrap();
  }, 'Failed to create workflow. Please try again.');

  if (!currentWorkflow) return null;

  return (
    <PageContainer>
      <Box display="flex" justifyContent="center">
        <Card sx={{
          maxWidth: 1400,
          width: '100%',
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
        }}>
          <WorkflowCreateHeader onSubmit={handleSubmit} onCancel={() => guardedNavigate('/workflows')} />

          <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
            {errorMessage && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
                {errorMessage}
              </Alert>
            )}

            <Stack spacing={3}>
              <BasicInfoSection />
              <ConditionsSection />
              <StepsSection />
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          {SUCCESS_MESSAGE}
        </Alert>
      </Snackbar>

      <UnsavedChangesDialog open={showDialog} onConfirm={confirmLeave} onCancel={cancelLeave} />
    </PageContainer>
  );
};

const WorkflowCreatePage: React.FC = () => (
  <WorkflowValidationProvider>
    <WorkflowCreateForm />
  </WorkflowValidationProvider>
);

export default WorkflowCreatePage;
